import type { ChipIntent } from '@/components/common/chip';

import type { Device, DeviceDisabledReason, DeviceStatus, SubscriptionStatus } from './types';

/**
 * Rounded, no-decimal relative time for "Last Synced" (story 101847, verbatim bands):
 * "30 secs ago" · "1 min ago" · "38 mins ago" · "1 hour ago" (>=60m,<2h) ·
 * "2 hours ago" (>=2h,<24h) · "1 day ago" (>=24h,<48h) · "2 days ago" (>=48h).
 */
export function formatLastSynced(iso: string | null, now: number = Date.now()): string | null {
  if (!iso) return null; // never synced → field not shown (no placeholder)
  const diffMs = Math.max(0, now - new Date(iso).getTime());
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return `${secs} sec${secs === 1 ? '' : 's'} ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Voltage display, e.g. 240 → "240v", 12.5 → "12.5v". */
export function formatVoltage(volts: number): string {
  return `${Number.isInteger(volts) ? volts : volts.toFixed(1)}v`;
}

const SUBSCRIPTION_STATUS_LABEL: Record<SubscriptionStatus, string> = {
  active: 'Active',
  expiring_soon: 'Expiring Soon',
  none: 'Inactive',
};

export function subscriptionStatusLabel(status: SubscriptionStatus): string {
  return SUBSCRIPTION_STATUS_LABEL[status];
}

/** "327 days 22 hrs" / "7 hrs 24 min" style runtime from minutes. */
export function formatRuntime(totalMinutes: number): string {
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const mins = totalMinutes % 60;
  if (days > 0) return `${days} day${days === 1 ? '' : 's'} ${hours} hr${hours === 1 ? '' : 's'}`;
  if (hours > 0) return `${hours} hr${hours === 1 ? '' : 's'} ${mins} min`;
  return `${mins} min`;
}

const STATUS_LABEL: Record<DeviceStatus, string> = {
  active: 'Active',
  idle: 'Idle',
  disconnected: 'Disconnected',
};

export function statusLabel(status: DeviceStatus): string {
  return STATUS_LABEL[status];
}

/** Map domain status → Chip colorway (Figma visual treatment). */
export function statusIntent(status: DeviceStatus): ChipIntent {
  switch (status) {
    case 'active':
      return 'approved'; // green / running
    case 'idle':
      return 'review'; // info blue / stopped
    case 'disconnected':
      return 'neutral'; // grey / offline
  }
}

/**
 * Why a card is disabled (non-interactive), or null when normal. OR logic over the
 * three conditions, in display priority (101847). Pending activation sorts last.
 */
export function disabledReason(device: Device): DeviceDisabledReason | null {
  if (!device.activated) return 'pending_activation';
  if (!device.configured) return 'not_configured';
  if (device.subscription.status === 'none') return 'no_subscription';
  return null;
}

export const DISABLED_TAG: Record<DeviceDisabledReason, string> = {
  not_configured: 'Not Configured',
  no_subscription: 'No Active Subscription',
  pending_activation: 'Pending activation by Greenway',
};

/**
 * Should the On/Off control be shown for this device (story 101847)?
 * Electric pumps (Hawk/G70) → yes. Diesel pumps → never. Sensors → never.
 * Disabled cards never show controls.
 */
export function hasPowerControl(device: Device): boolean {
  if (disabledReason(device)) return false;
  return device.kind === 'pump' && device.fuelType === 'Electric';
}

/**
 * On/Off button label. Idle → "On"; Active → "Off"; Disconnected reflects the last
 * known state (Off if it was running, On if it was idle).
 */
export function powerActionLabel(device: Device): 'On' | 'Off' {
  if (device.status === 'active') return 'Off';
  if (device.status === 'idle') return 'On';
  return device.powerOn ? 'Off' : 'On';
}

/**
 * Human "expected wait" for a queued command, from the device's check-in interval
 * (story 101849, AC5). e.g. 10 → "about 10 min", 1 → "about a minute", 60 → "about an
 * hour", 90 → "about 2 hrs". Communicated in the confirmation before the grower commits.
 */
export function expectedWaitLabel(device: Device): string {
  const mins = Math.max(1, Math.round(device.checkInIntervalMinutes));
  if (mins === 1) return 'about a minute';
  if (mins < 60) return `about ${mins} min`;
  const hours = Math.round(mins / 60);
  return hours === 1 ? 'about an hour' : `about ${hours} hrs`;
}

/** Card status pill (per the Figma device card): Running / Stopped / Offline. The
 *  card uses operational wording + the design's colorways (running = info blue,
 *  stopped = brand green, offline = neutral), distinct from the domain status chip. */
export type CardStatusTone = 'running' | 'stopped' | 'offline';

export function cardStatus(device: Device): { text: string; tone: CardStatusTone } {
  switch (device.status) {
    case 'active':
      return { text: 'Running', tone: 'running' };
    case 'idle':
      return { text: 'Stopped', tone: 'stopped' };
    case 'disconnected':
      return { text: 'Offline', tone: 'offline' };
  }
}

/** Human label for the device's type line on cards / detail header. */
export function deviceTypeLabel(device: Device): string {
  if (device.kind === 'sensor') return 'Soil Sensor';
  return device.fuelType === 'Diesel' ? 'Diesel Pump' : 'Electric Pump';
}

/**
 * Fleet summary for the profile menu (Figma 2327-693027). `total` = every device;
 * `active` = devices that are online (status not Disconnected); `running` = devices
 * currently running (status Active). Active ⊇ running.
 */
export type DeviceStats = { total: number; active: number; running: number };

export function deviceStats(devices: Device[]): DeviceStats {
  return {
    total: devices.length,
    active: devices.filter((d) => d.status !== 'disconnected').length,
    running: devices.filter((d) => d.status === 'active').length,
  };
}

/** Figma quick-tab keys (All / Diesel / Electric / Sensors). */
export type DeviceTab = 'all' | 'diesel' | 'electric' | 'sensors';

export function matchesTab(device: Device, tab: DeviceTab): boolean {
  switch (tab) {
    case 'all':
      return true;
    case 'diesel':
      return device.fuelType === 'Diesel';
    case 'electric':
      return device.fuelType === 'Electric';
    case 'sensors':
      return device.kind === 'sensor';
  }
}
